import { operators as o } from '@pendo/aggregations';
import { generateAggregationSegment } from '@pendo/middleware';
import { psHighchartsConfig } from './ps-highcharts-config';

export const APP_ID = 'APP_ID';

export const DATE_RANGE = 'DATE_RANGE';

export const DATE_RANGE_ITERATIVE = 'DATE_RANGE_ITERATIVE';

export const ACCOUNT_ID = 'ACCOUNT_ID';

export const SEGMENT_ID = 'SEGMENT_ID';

export const PS_CUSTOM_WIDGET_SETTINGS = {
    dateRange: {
        type: 'Last 30 Days',
    },
    period: 'daily',
    segmentId: 'everyone',
    account: {
        id: '',
        text: 'All Accounts',
    },
    appId: 'expandAppIds("*")',
    config: JSON.stringify(psHighchartsConfig),
};

export const PS_CUSTOM_WIDGET_SETTINGS_AEU = {
    dateRange: {
        type: 'Last 30 Days',
    },
    period: 'daily',
    segmentId: 'everyone',
    appId: 'expandAppIds("*")',
    config: JSON.stringify(psHighchartsConfig),
};

function isObject (value) {
    return !!value && typeof value === 'object';
}

function isFilterPlaceholder (object) {
    if (!isObject(object)) return false;

    return 'filters' in object;
}

function isAppPlaceholder (object) {
    if (!isObject(object?.source)) return false;

    const foundAppPlaceholder = Object.keys(object.source).some(
        (key) => isObject(object.source[key]) && object.source[key]?.appId === APP_ID,
    );

    return foundAppPlaceholder;
}

function isTimeSeriesPlaceholder (source) {
    if (!isObject(source)) return false;

    return source.timeSeries === DATE_RANGE || source.timeSeries === DATE_RANGE_ITERATIVE;
}

function isSourcePlaceholder (object) {
    if (!isObject(object)) return false;

    if ('source' in object) {
        const { source } = object;

        return isAppPlaceholder(object) || isTimeSeriesPlaceholder(source);
    }

    return false;
}

function generateAppFilter (filter, appId) {
    if (!isAppPlaceholder(filter)) return;

    Object.keys(filter.source).forEach((key) => {
        if (isObject(filter.source[key])) {
            if (!appId) {
                delete filter.source[key].appId;
            } else {
                filter.source[key].appId = appId;
            }
        }
    });

    return filter;
}

function generateDateRangeFilter (filter, timeSeries) {
    const filterId = filter.source.timeSeries;
    if (!timeSeries) {
        filter.source.timeSeries = {
            first: 'now()',
            count: -30,
            period: filterId === DATE_RANGE_ITERATIVE ? 'daily' : 'dayRange',
        };

        return filter;
    }
    filter.source.timeSeries = timeSeries;
    if (filterId === DATE_RANGE_ITERATIVE) {
        filter.source.timeSeries.period =
            filter.source.timeSeries.period === 'dayRange'
                ? 'daily'
                : filter.source.timeSeries.period.replace('Range', 'ly');
    }

    return filter;
}

function generateSegmentFilter (segmentId, hasShowAnonymousFlag) {
    if (!segmentId) return;

    return generateAggregationSegment({ segmentId, hasShowAnonymousFlag }).filter((s) => s !== undefined);
}

function generateAccountFilter (accountId) {
    if (!accountId) return;

    // eslint-disable-next-line no-useless-escape
    return o.filter(`accountId == \"${accountId}\"`);
}

function replaceSourcePlaceholder (placeholder, dashboardFilters = {}) {
    const { appId, timeSeries } = dashboardFilters;
    if (!isSourcePlaceholder(placeholder)) return [placeholder];

    let finalSource = placeholder;
    if (isAppPlaceholder(placeholder)) {
        finalSource = generateAppFilter(placeholder, appId);
    }

    if (placeholder.source.timeSeries === DATE_RANGE || placeholder.source.timeSeries === DATE_RANGE_ITERATIVE) {
        finalSource = generateDateRangeFilter(placeholder, timeSeries);
    }

    return [finalSource];
}

function replaceFilterPlaceholder (placeholder, dashboardFilters = {}, hasShowAnonymousFlag = false) {
    const { segmentId, account } = dashboardFilters;

    if (!isFilterPlaceholder(placeholder)) {
        return placeholder;
    }

    let pipelineInsert = [];
    placeholder.filters.forEach((filter) => {
        switch (filter) {
            case SEGMENT_ID: {
                const segmentFilter = generateSegmentFilter(segmentId, hasShowAnonymousFlag);

                if (segmentFilter && segmentFilter.length) {
                    pipelineInsert = [...pipelineInsert, ...segmentFilter];
                }

                break;
            }
            case ACCOUNT_ID: {
                const accountFilter = generateAccountFilter(account?.id);

                if (accountFilter) {
                    pipelineInsert.push(accountFilter);
                }

                break;
            }
        }
    });

    return pipelineInsert;
}

export function findAndReplaceFilterPlaceholders (
    pipeline,
    dashboardFilters,
    nested = false,
    hasShowAnonymousFlag = false,
) {
    if (!pipeline) {
        return;
    }

    const finalPipeline = pipeline.reduce((acc, option) => {
        if (isFilterPlaceholder(option)) {
            acc = [...acc, ...replaceFilterPlaceholder(option, dashboardFilters, hasShowAnonymousFlag)];
        } else if (isSourcePlaceholder(option)) {
            acc = [...acc, ...replaceSourcePlaceholder(option, dashboardFilters)];
        } else if ('merge' in option && 'pipeline' in option.merge) {
            const nestedPipeline = findAndReplaceFilterPlaceholders(
                option.merge.pipeline,
                dashboardFilters,
                true,
                hasShowAnonymousFlag,
            );
            if (nestedPipeline) {
                const updatedOption = { ...option, merge: { ...option.merge, pipeline: nestedPipeline } };
                acc.push(updatedOption);
            } else {
                acc.push(option);
            }
        } else if ('spawn' in option) {
            const updatedSpawnPipelines = [];
            option.spawn?.forEach((spawnPipeline) => {
                const nestedSpawn = findAndReplaceFilterPlaceholders(
                    spawnPipeline,
                    dashboardFilters,
                    true,
                    hasShowAnonymousFlag,
                );
                if (nestedSpawn) {
                    updatedSpawnPipelines.push(nestedSpawn);
                } else {
                    updatedSpawnPipelines.push(spawnPipeline);
                }
            });
            acc.push({ ...option, spawn: updatedSpawnPipelines });
        } else {
            acc.push(option);
        }

        return acc;
    }, []);

    const isBlacklistOnly = dashboardFilters?.segmentId === 'blacklistOnly';
    const pipelineHasSegmentPlaceholder = JSON.stringify(pipeline).includes(SEGMENT_ID);
    if (!nested && isBlacklistOnly && pipelineHasSegmentPlaceholder) {
        return o.build(o.settings({ excludelist: 'only' }), o.pipeline(finalPipeline));
    }

    return o.pipeline(finalPipeline);
}
